// src/Page/Dashboard.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import axios from "axios";
import api, { resolveUploadUrl } from "../api"; // ✅ FIX: This was missing

import {
  getUserJournal,
  getMT5Account,
  getMT5Trades,
  sendAIChat,
  WS_BASE,
  getOnboardingStatus,
  completeTutorial,
} from "../api";
import AIDraggableChatButton from "../components/AIDraggableChatButton";
import PremiumBadge from "../components/PremiumBadge";
import Tutorial from "../components/Tutorial";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
} from "recharts";
import {
  MessageCircle,
  Settings,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  History,
  PieChart,
  BookOpen,
  Wallet,
  Percent,
  TrendingUpIcon,
  X,
  Plus,
  Bell,
  ChevronDown,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Filter,
  BarChart3,
  AlertTriangle,
  AlertCircle,
  CalendarClock,
  CheckCircle, 
  Info,        
  UserPlus,   
  Heart,
  CandlestickChart,
  Scale
} from "lucide-react";


// ============================================================
// ✅ SECURITY HELPER (DEFINE OUTSIDE COMPONENT)
// ============================================================
const sanitizeInput = (input) => {
  if (!input) return "";
  return String(input)
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};


// --- Custom Hook: Silent Pull to Refresh ---
// (Now shared across pages — see src/hooks/usePullToRefresh.js)

// --- Smart Polling Helper ---
function useInterval(callback, delay) {
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

// --- Chat Modal Component (Light Mode Ready) ---
const ChatModal = ({
  isOpen,
  onClose,
  chatHistory,
  chatTyping,
  chatMessage,
  setChatMessage,
  onSubmit,
}) =>
  isOpen && (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/70 flex justify-center items-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 backdrop-blur-sm border border-gray-200 dark:border-gray-800 rounded-lg w-11/12 md:w-[28rem] p-6 shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-medium text-blue-600 dark:text-blue-300 flex items-center">
            <MessageCircle size={20} className="mr-2" />
            AI Assistant
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={22} />
          </button>
        </div>
        <div className="bg-gray-100 dark:bg-gray-800/50 rounded-lg p-3 h-80 overflow-y-auto mb-4">
          {chatHistory.map((m, i) => (
            <div
              key={i}
              className={`mb-3 ${m.role === "user" ? "text-right" : "text-left"}`}
            >
              <div
                className={`inline-block px-4 py-2 rounded-lg max-w-[80%] text-base ${
                  m.role === "user"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {chatTyping && (
            <div className="text-left">
              <div className="inline-block px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-200 max-w-[80%] text-base">
                <div className="flex items-center">
                  <div className="animate-pulse flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animation-delay-200"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animation-delay-400"></div>
                  </div>
                  <span className="ml-2 text-sm">AI is typing...</span>
                </div>
              </div>
            </div>
          )}
        </div>
        <form onSubmit={onSubmit} className="flex gap-2">
          <input
            value={chatMessage}
            onChange={(e) => setChatMessage(e.target.value)}
            placeholder="Ask about trading..."
            className="flex-grow p-4 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white text-base focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
          />
          <button
            type="submit"
            className="px-5 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9-18 2z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19V9"
              />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );

const NotificationItem = ({ notification, onRemove, onMarkRead, navigate }) => {
  const { id, type, title, body, created_at, is_read, actor_name, actor_id, post_id, post_author_id, url } = notification;

  const message = body;
  const timestamp = created_at;

  const handleClick = () => {
    if (!is_read) onMarkRead(id);

    // Navigate based on notification type
    if (type === 'message' && actor_id) {
      navigate(`/dashboard/messages/${actor_id}`);
    } else if (type === 'market_move' || type === 'market_event' || type === 'market_bias') {
      navigate(url || '/dashboard/markets');
    } else if (type === 'follow' && actor_id) {
      navigate(`/dashboard/profile/${actor_id}`);
    } else if (['like', 'comment', 'repost', 'mention'].includes(type) && post_id) {
      if (post_author_id) {
        navigate(`/dashboard/profile/${post_author_id}?postId=${post_id}`);
      } else {
        navigate(`/dashboard/community?postId=${post_id}`);
      }
    }
  };

  const getIcon = () => {
    switch (type) {
      case "trade":
        const isProfit = message && message.toLowerCase().includes("profit");
        return isProfit 
          ? <TrendingUp size={18} className="text-green-500 dark:text-green-400" />
          : <TrendingDown size={18} className="text-red-500 dark:text-red-400" />;
      case "behavior_alert":
        // Yellow/Amber for warnings (LOW/MODERATE)
        return <AlertTriangle size={18} className="text-amber-500 dark:text-amber-400" />;
      case "safety_net":
        // Red for Strikes and Bans (HIGH/CRITICAL)
        return <AlertCircle size={18} className="text-red-500 dark:text-red-400" />;
      case "follow":
        return <UserPlus size={18} className="text-blue-500 dark:text-blue-400" />;
      case "market_move":
        return <TrendingUp size={18} className="text-sky-500 dark:text-sky-400" />;
      case "market_event":
        return <CalendarClock size={18} className="text-violet-500 dark:text-violet-400" />;
      case "market_bias":
        return <Scale size={18} className="text-amber-500 dark:text-amber-400" />;
      case "like":
        return <Heart size={18} className="text-pink-500 dark:text-pink-400" />;
      case "success":
        return <CheckCircle size={18} className="text-green-500 dark:text-green-400" />;
      case "error":
        return <AlertCircle size={18} className="text-red-500 dark:text-red-400" />;
      default:
        return <Bell size={18} className="text-gray-400" />;
    }
  };


  return (
    <div
      onClick={handleClick}
      className={`p-4 rounded-lg border mb-2 transform transition-all duration-300 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
        !is_read
          ? "bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800"
          : "bg-white dark:bg-gray-800/50 border-gray-200 dark:border-gray-700/50"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start">
          <div className="mr-3 mt-0.5">{getIcon()}</div>
          <div>
            <h4 className="text-gray-900 dark:text-white font-medium text-base">
              {title || actor_name || "System"}
            </h4>
            <p className="text-gray-600 dark:text-gray-300 text-sm mt-1">
              {message}
            </p>
            <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
              {new Date(timestamp).toLocaleTimeString()}
            </p>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(id); }}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 ml-4"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

// --- Trading Behavior Analysis Component (With Resolve Functionality) ---
const TradingBehaviorAnalysis = ({ alerts, onResolve }) => {
  
  // Helper to format the alert type for display
  const formatTitle = (type) => {
    if (!type) return "Unknown Alert";
    return type.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper to determine border/bg colors based on severity
  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return "border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30";
      case 'high':
        return "border-red-200 dark:border-red-700/50 bg-red-50 dark:bg-red-900/20";
      case 'medium':
        return "border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20";
      default:
        return "border-blue-200 dark:border-blue-700/50 bg-blue-50 dark:bg-blue-900/20";
    }
  };

  if (!alerts || alerts.length === 0) {
    return (
      <div className="text-gray-400 dark:text-gray-500 text-center py-12 bg-gray-50 dark:bg-gray-800/20 rounded-lg border border-gray-200 dark:border-gray-700/30">
        <div className="flex flex-col items-center">
          <CheckCircle size={32} className="mb-3 text-green-400 dark:text-green-500" />
          <span className="text-base font-medium">All Clear!</span>
          <span className="text-sm text-gray-400 mt-1">No behavioral issues detected.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {alerts.map((alert, index) => {
        // Don't render if resolved (optional: or render faded)
        if (alert.resolved) return null;

        return (
          <div
            key={alert.id || index}
            className={`p-4 rounded-xl border transition-all duration-300 flex justify-between items-start ${getSeverityStyles(alert.severity)}`}
          >
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className="mt-0.5">
                {alert.severity === 'critical' || alert.severity === 'high' 
                  ? <AlertTriangle size={20} className="text-red-500 dark:text-red-400" /> 
                  : <Activity size={20} className="text-amber-500 dark:text-amber-400" />
                }
              </div>

              {/* Content */}
              <div className="flex-grow">
                <div className="flex justify-between items-start mb-1">
                  <h4 className="text-gray-900 dark:text-white font-semibold text-base">
                    {formatTitle(alert.type)}
                  </h4>
                  <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                    {new Date(alert.date).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                  {alert.description}
                </p>
              </div>
            </div>

            {/* Resolve Button */}
            {onResolve && (
              <button 
                onClick={() => onResolve(alert.id)}
                className="ml-4 p-2 rounded-lg hover:bg-white/50 dark:hover:bg-black/20 transition-colors group"
                title="Mark as Resolved"
              >
                <CheckCircle 
                  size={20} 
                  className="text-gray-300 dark:text-gray-600 group-hover:text-green-500 dark:group-hover:text-green-400 transition-colors" 
                />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

// --- Metrics Modal Component (Fixed Date Handling) ---
const MetricsModal = ({ isOpen, onClose, trades, account }) => {
  const tradesArray = Array.isArray(trades) ? trades : [];

  // ✅ HELPER: Safely convert Date String or Timestamp to Milliseconds
  const safeGetTime = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') {
      // If it's a number, check if it's seconds or milliseconds
      // Timestamps in seconds are usually < 10000000000 (year 2286)
      return val > 9999999999 ? val : val * 1000;
    }
    // If it's a string (ISO Format), parse it
    return new Date(val).getTime();
  };

  const grossProfit = React.useMemo(
    () =>
      tradesArray
        .filter((t) => (Number(t.profit) || 0) > 0)
        .reduce((sum, t) => sum + Number(t.profit) || 0, 0),
    [tradesArray]
  );
  const grossLoss = React.useMemo(
    () =>
      tradesArray
        .filter((t) => (Number(t.profit) || 0) < 0)
        .reduce((sum, t) => sum + Number(t.profit) || 0, 0),
    [tradesArray]
  );
  const totalProfit = React.useMemo(
    () => tradesArray.reduce((sum, t) => sum + Number(t.profit) || 0, 0),
    [tradesArray]
  );

  const profitFactor = React.useMemo(() => {
    const absLoss = Math.abs(grossLoss);
    return absLoss === 0 ? (grossProfit > 0 ? Infinity : 0) : grossProfit / absLoss;
  }, [grossProfit, grossLoss]);

  const maxDrawdown = React.useMemo(() => {
    const closedTrades = tradesArray.filter(
      (t) => t.state === "closed" || t.profit !== undefined
    );
    if (closedTrades.length === 0) return { amount: 0, percent: 0 };
    
    // Sort by date safely
    const sorted = [...closedTrades].sort(
      (a, b) => safeGetTime(a.close_time || a.time) - safeGetTime(b.close_time || b.time)
    );
    
    const startBalance = (account?.balance || 0) - totalProfit;
    let peak = startBalance,
      maxDdVal = 0,
      equity = startBalance;
    sorted.forEach((trade) => {
      equity += Number(trade.profit) || 0;
      if (equity > peak) peak = equity;
      const dd = peak - equity;
      if (dd > maxDdVal) maxDdVal = dd;
    });
    return { amount: maxDdVal, percent: peak > 0 ? (maxDdVal / peak) * 100 : 0 };
  }, [tradesArray, account, totalProfit]);

  const maxDepositLoad = React.useMemo(() => {
    const startBalance = (account?.balance || 0) - totalProfit;
    return startBalance <= 0 ? 0 : (maxDrawdown.amount / startBalance) * 100;
  }, [maxDrawdown, account, totalProfit]);

  // ✅ FIXED: Trades Per Week Calculation
  const tradesPerWeek = React.useMemo(() => {
    if (tradesArray.length === 0) return 0;
    
    // Map to timestamps in milliseconds
    const times = tradesArray
      .map((t) => safeGetTime(t.close_time || t.time))
      .filter(t => t > 0);

    if (times.length === 0) return tradesArray.length; // Fallback if no valid dates
    
    const minTime = Math.min(...times);
    const maxTime = Math.max(...times);
    
    // Difference in Milliseconds -> Days
    const diffDays = (maxTime - minTime) / (1000 * 60 * 60 * 24);
    
    // If all trades happened on same day, diff is 0. Treat as 1 day span.
    const days = Math.max(diffDays, 1);
    
    return (tradesArray.length / (days / 7)).toFixed(1);
  }, [tradesArray]);

  // ✅ FIXED: Average Hold Time Calculation
  const averageHoldTime = React.useMemo(() => {
    const validTrades = tradesArray.filter(
      (t) => t.open_time && (t.close_time || t.time)
    );
    if (validTrades.length === 0) return "—";
    
    // Calculate average duration in Milliseconds
    const totalDurationMs = validTrades.reduce((sum, t) => {
      const openTs = safeGetTime(t.open_time);
      const closeTs = safeGetTime(t.close_time || t.time);
      return sum + (closeTs - openTs);
    }, 0);

    const avgSec = totalDurationMs / validTrades.length / 1000; // Convert ms to seconds

    const h = Math.floor(avgSec / 3600);
    const m = Math.floor((avgSec % 3600) / 60);
    
    if (isNaN(h) || isNaN(m)) return "—";
    return h > 24 ? `${Math.floor(h / 24)}d ${h % 24}h` : `${h}h ${m}m`;
  }, [tradesArray]);

  const recoveryFactor = React.useMemo(
    () =>
      maxDrawdown.amount === 0
        ? totalProfit > 0
          ? Infinity
          : 0
        : totalProfit / maxDrawdown.amount,
    [totalProfit, maxDrawdown]
  );

  const assetTypes = React.useMemo(() => {
    const map = tradesArray.reduce((acc, t) => {
      const sym = (t.symbol || "").toUpperCase();
      let type = "Currency";
      if (sym.includes("XAU") || sym.includes("GOLD")) type = "Commodities";
      else if (sym.includes("BTC") || sym.includes("ETH")) type = "Crypto";
      else if (sym.includes("30") || sym.includes("500")) type = "CFD";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    const colors = {
      Currency: "#3B82F6",
      Crypto: "#F59E0B",
      Commodities: "#EF4444",
      CFD: "#8B5CF6",
    };
    return Object.entries(map).map(([name, value]) => ({
      name,
      value,
      color: colors[name] || "#6B7280",
    }));
  }, [tradesArray]);

  if (!isOpen) return null;
  const fmt = (n, d = 2) => (n == null ? "—" : Number(n).toFixed(d));

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/70 flex justify-center items-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 backdrop-blur-sm border border-gray-200 dark:border-gray-800 rounded-lg w-full max-w-7xl h-[90vh] overflow-hidden shadow-2xl">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <TrendingUp className="mr-2 text-blue-500" size={28} />
            Trading Analytics
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={26} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto h-[calc(100%-80px)] pb-16 bg-slate-50 dark:bg-gray-900">
          {/* Metrics Content */}
          <div className="mb-8">
            <h3 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Performance Metrics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Profit Factor
                  </span>
                  <Activity size={18} className="text-blue-500" />
                </div>
                <div className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">
                  {profitFactor === Infinity ? "∞" : fmt(profitFactor)}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Max Drawdown
                  </span>
                  <ArrowDownRight className="text-red-500" size={18} />
                </div>
                <div className="text-xl md:text-3xl font-bold text-red-500">
                  {fmt(maxDrawdown.amount)}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Recovery Factor
                  </span>
                  <TrendingUp size={18} className="text-green-500" />
                </div>
                <div className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">
                  {recoveryFactor === Infinity ? "∞" : fmt(recoveryFactor)}
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Max Deposit Load
                  </span>
                  <Percent size={18} className="text-amber-500" />
                </div>
                <div className="text-xl md:text-3xl font-bold text-amber-500">
                  {fmt(maxDepositLoad)}%
                </div>
              </div>
            </div>
          </div>
          {/* Activity & Efficiency */}
          <div className="mb-8">
            <h3 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Activity & Efficiency
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
              <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Frequency
                  </span>
                  <Calendar size={18} className="text-purple-500 dark:text-purple-400" />
                </div>
                <div className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">
                  {tradesPerWeek}
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Trades per week
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Hold Time
                  </span>
                  <History size={18} className="text-cyan-500 dark:text-cyan-400" />
                </div>
                <div className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white">
                  {averageHoldTime}
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Average duration
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Net Profit
                  </span>
                  <DollarSign size={18} className="text-green-500 dark:text-green-400" />
                </div>
                <div
                  className={`text-xl md:text-3xl font-bold ${
                    totalProfit >= 0
                      ? "text-green-500 dark:text-green-400"
                      : "text-red-500 dark:text-red-400"
                  }`}
                >
                  {totalProfit >= 0 ? "+" : ""}
                  {fmt(totalProfit)}
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Total realized
                </p>
              </div>
            </div>
          </div>

          {/* Asset Distribution */}
          <div>
            <h3 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
              Asset Distribution
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-7">
              <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 md:p-5 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  By Asset Class
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <RechartsPieChart>
                    <Pie
                      data={assetTypes}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {assetTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} trades`, "Count"]} />
                  </RechartsPieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {assetTypes.map((item, index) => (
                    <div key={index} className="flex items-center">
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: item.color }}
                      ></div>
                      <span className="text-gray-600 dark:text-gray-300 text-sm">
                        {item.name} ({item.value})
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-none flex flex-col justify-center">
                <h4 className="text-gray-900 dark:text-white font-medium mb-4">
                  Summary
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Total Trades:
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {tradesArray.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Winning:
                    </span>
                    <span className="text-green-500 dark:text-green-400 font-medium">
                      {tradesArray.filter((t) => Number(t.profit) > 0).length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Losing:</span>
                    <span className="text-red-500 dark:text-red-400 font-medium">
                      {tradesArray.filter((t) => Number(t.profit) < 0).length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">
                      Win Rate:
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {tradesArray.length > 0
                        ? (
                            (tradesArray.filter((t) => Number(t.profit) > 0).length /
                              tradesArray.length) *
                            100
                          ).toFixed(1)
                        : 0}
                      %
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
// --- Main Dashboard Component ---
export default function Dashboard() {
  const navigate = useNavigate();
  const { currentUser, refreshUser } = useAuth();
  // Layout context (shell provides market access + notification state)
  const outletCtx = useOutletContext() || {};
  const hasMarketAccess = Boolean(outletCtx.marketAccess);

  // State management
  const [account, setAccount] = useState(null);
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [displayAccount, setDisplayAccount] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [chatTyping, setChatTyping] = useState(false);
  const [activeTab, setActiveTab] = useState("history");
  const [showTutorial, setShowTutorial] = useState(false);
  const [loadingTutorial, setLoadingTutorial] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [metricsOpen, setMetricsOpen] = useState(false);
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [behaviorAlerts, setBehaviorAlerts] = useState([]);

  // Refs
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const tradesRef = useRef(trades);

  // Helper functions
  const fmt = (n, d = 2) =>
    n == null
      ? "—"
      : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });
  const safe = (v) => (v == null ? 0 : Number(v));

  const tradesArray = Array.isArray(trades) ? trades : [];

  // Derived Data
  const todayTrades = tradesArray.filter((t) => {
    if (!t.time && !t.trade_time) return false;
    const tradeDate = new Date((t.time || t.trade_time) * 1000);
    const today = new Date();
    return (
      tradeDate.getDate() === today.getDate() &&
      tradeDate.getMonth() === today.getMonth() &&
      tradeDate.getFullYear() === today.getFullYear()
    );
  });

  const lastFiveTrades = [...tradesArray]
    .filter(
      (t) =>
        (t.close_price && Number(t.close_price) > 0) || t.state === "closed"
    )
    .sort(
      (a, b) =>
        (b.close_time || b.time_close || b.time || b.trade_time || 0) -
        (a.close_time || a.time_close || a.time || a.trade_time || 0)
    )
    .slice(0, 5);

  const totalProfit = tradesArray.reduce((s, t) => s + safe(t.profit), 0);
  const todayProfit = todayTrades.reduce((s, t) => s + safe(t.profit), 0);
  const winRate = tradesArray.length
    ? Math.round(
        (tradesArray.filter((t) => safe(t.profit) > 0).length / tradesArray.length) *
          100
      )
    : 0;
  const todayWinRate = todayTrades.length
    ? Math.round(
        (todayTrades.filter((t) => safe(t.profit) > 0).length / todayTrades.length) *
          100
      )
    : 0;


 


  // Notification Logic
   // --- NEW: Backend Notification Logic ---
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications/', { params: { limit: 20 } });
      const notifs = res.data || [];
      setNotifications(notifs);
      
      // Calculate unread count
      const unread = notifs.filter(n => !n.is_read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    }
  }, []);

  const removeNotification = useCallback(async (id) => {
    // Optimistic update
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    try {
      await api.delete(`/notifications/${id}`);
    } catch (err) {
      console.error("Failed to delete notification", err);
    }
  }, []);

  const markAsRead = useCallback(async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? {...n, is_read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark as read", err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({...n, is_read: true})));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all read", err);
    }
  }, []);

  // Data Fetching & WebSocket
  const disconnectWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }
  }, []);

 
// ✅ ADD this new function
const fetchBehaviorAnalysis = useCallback(async () => {
  try {
    // Call the new Unified AI Endpoint
    const res = await api.get('/ai/analysis'); 
    
    // The new endpoint returns { issues: [...] }
    // Map it to the format expected by the UI
    const formattedAlerts = (res.data.issues || []).map(issue => ({
        id: issue.rule, // Use rule name as ID for now
        type: issue.rule.toLowerCase().replace(/ /g, "_"),
        severity: issue.severity.toLowerCase(),
        description: issue.message,
        date: new Date().toISOString(),
        resolved: false
    }));

    setBehaviorAlerts(formattedAlerts);
  } catch (err) {
    console.error("Failed to fetch behavior analysis", err);
  }
}, []);

   // Centralized Refresh Function
  const refreshData = useCallback(async () => {
    try {
      const [acctRes, tradesRes] = await Promise.all([
        getMT5Account({ timeout: 30000 }),
        getMT5Trades(),
      ]);
      if (acctRes?.data) {
        setAccount(acctRes.data);
        setDisplayAccount(acctRes.data);
      }
      if (tradesRes?.data && Array.isArray(tradesRes.data))
        setTrades(tradesRes.data);
      
      fetchNotifications();
      fetchBehaviorAnalysis(); // ✅ Correct function name

    } catch (err) {
      if (err.name !== "AbortError") console.warn("Refresh failed:", err.message);
    }
  }, [fetchNotifications, fetchBehaviorAnalysis]); 

  const scheduleReconnect = useCallback(
    (delay = 5000) => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => connectWebSocketWithRetry(), delay);
    },
    []
  );

  const connectWebSocketWithRetry = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      disconnectWs();
      setTimeout(() => {
        const ws = new WebSocket(`${WS_BASE}?token=${token}`);
        wsRef.current = ws;
        ws.onopen = () => console.log("✅ WS Connected");
        ws.onclose = () => {
          // Use console log instead of addNotification
          console.log("⚠️ WS Disconnected. Reconnecting...");
          scheduleReconnect(5000);
        };
        ws.onerror = () => {
          try {
            ws.close();
          } catch {}
        };
     // Inside connectWebSocketWithRetry...
  // Inside src/Page/Dashboard.jsx -> connectWebSocketWithRetry -> ws.onmessage

ws.onmessage = async (evt) => {
    try {
        const payload = JSON.parse(evt.data);
        
        // 1. Handle Standard Account Updates
        if (payload.event === "mt5_update") {
            refreshData(); // Refreshes the trade list
        }

        // --- ✅ NEW: Handle Trade Notifications ---
        if (payload.event === "new_trade" || payload.event === "closed_trade") {
            const tradeData = payload.data; // Expecting { symbol, profit, type, ticket ... }
            
            // A. Determine Profit/Loss for UI
            const isProfit = tradeData.profit >= 0;
            const tradeType = isProfit ? "Profit" : "Loss";
            const iconType = isProfit ? "success" : "error";

            // B. Add to Bell Icon Notifications
            setNotifications(prev => [{
                id: tradeData.ticket || Date.now(),
                type: "trade", // Maps to TrendingUp/Down in NotificationItem
                title: `Trade ${payload.event === "new_trade" ? "Opened" : "Closed"}`,
                body: `${tradeData.symbol} ${tradeData.action || ''} - P/L: $${tradeData.profit}`,
                created_at: new Date().toISOString(),
                is_read: false
            }, ...prev]);
            
            // C. Increment Unread Count
            setUnreadCount(prev => prev + 1);

            // D. (Optional) Trigger In-App Sound
            // const audio = new Audio('/sounds/notification.mp3');
            // audio.play().catch(e => console.log("Audio play failed", e));
        }

        // 2. Handle Behavior Alerts
        if (payload.event === "behavior_alert") {
            const alertData = payload.data;
            // ... existing alert logic ...
        }

        // 3. Handle Account Lock
        if (payload.event === "account_locked") {
            refreshUser(); 
        }

    } catch (e) {
        console.error("WS Msg Error", e);
    }
};

      }, 100);
    } catch (err) {
      scheduleReconnect(5000);
    }
  }, [disconnectWs, scheduleReconnect, refreshData]); // Updated dependencies

  // Hook Integration
  const containerRef = usePullToRefresh(refreshData);

  // Effects
   useEffect(() => {
    let mounted = true;
    const abortController = new AbortController();
     const loadAll = async () => {
      try {
        const [acctRes, tradesRes] = await Promise.all([
          getMT5Account({ signal: abortController.signal }),
          getMT5Trades({ signal: abortController.signal }),
        ]);
        if (!mounted) return;
        if (acctRes?.data) {
          setAccount(acctRes.data);
          setDisplayAccount(acctRes.data);
        }
        if (tradesRes?.data) setTrades(tradesRes.data);
        
        // Fetch initial notifications
        fetchNotifications();
     fetchBehaviorAnalysis();
        
      } catch (err) {
        // ...
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadAll();
    connectWebSocketWithRetry();
    return () => {
      mounted = false;
      abortController.abort();
      disconnectWs();
    };
 }, [connectWebSocketWithRetry, disconnectWs, fetchBehaviorAnalysis]);// Added fetchAlerts

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await getOnboardingStatus();
        if (res.data.hasSeenDashboardTutorial === false) setShowTutorial(true);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingTutorial(false);
      }
    };
    fetchStatus();
  }, []);

    // ---------------------------------------------------------
  // 🔄 FRONTEND TRADE WATCHER (Detects changes & triggers AI)
  // ---------------------------------------------------------
  
  // 1. Ref to store the previous snapshot of trades
  const prevTradesRef = useRef([]);

  useEffect(() => {
    // Only run if we have trades data
    if (!trades || trades.length === 0) return;

    const prevTrades = prevTradesRef.current;
    const currentTickets = new Set(trades.map(t => t.ticket));
    const prevTickets = new Set(prevTrades.map(t => t.ticket));

    // 2. Detect NEW OPEN TRADES (Exists in current, not in prev)
    trades.forEach(trade => {
      if (!prevTickets.has(trade.ticket) && trade.state === 'open') {
        console.log("🚀 New trade detected by frontend:", trade);
        
        // Send to AI for analysis/notification
        api.post('/ai/handle-trade-event', { 
          event: 'new_trade', 
          data: trade 
        }).catch(err => console.error("AI Event Error", err));
      }
    });

    // 3. Detect NEWLY CLOSED TRADES (Exists in both, but state changed)
    trades.forEach(trade => {
      const prevTrade = prevTrades.find(t => t.ticket === trade.ticket);
      
      // Check if it was open before and is closed now
      if (prevTrade && prevTrade.state === 'open' && trade.state === 'closed') {
        console.log("💰 Closed trade detected by frontend:", trade);

        // Send to AI for analysis/notification
        api.post('/ai/handle-trade-event', { 
          event: 'closed_trade', 
          data: trade 
        }).catch(err => console.error("AI Event Error", err));
      }
    });

    // 4. Update the ref for the next check
    prevTradesRef.current = trades;

  }, [trades]); // Runs whenever 'trades' changes


  useInterval(() => {
    if (isTabVisible) refreshData();
  }, isTabVisible ? 10000 : null);

   const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim()) return;

    // ✅ SECURITY: Sanitize the message before processing
    const rawMsg = chatMessage.trim();
    const safeMsg = sanitizeInput(rawMsg);

    // Optional: Prevent sending if message was purely malicious code
    if (safeMsg.length === 0) {
        setChatMessage("");
        return;
    }

    setChatMessage(""); // Clear input immediately
    setChatHistory((h) => [...h, { role: "user", text: safeMsg }]); // Show sanitized version
    setChatTyping(true);

    try {
      // Send sanitized message to backend
      const res = await sendAIChat(safeMsg);
      
      // Sanitize AI response as well just in case
      const aiResponse = res?.data?.response ?? "No response";
      const safeResponse = sanitizeInput(aiResponse);

      setChatHistory((h) => [
        ...h,
        { role: "assistant", text: safeResponse },
      ]);
    } catch (err) {
      setChatHistory((h) => [...h, { role: "assistant", text: "Error contacting AI" }]);
    } finally {
      setChatTyping(false);
    }
  };

  const handleTutorialClose = async () => {
    setShowTutorial(false);
    try {
      await completeTutorial("dashboard");
      if (refreshUser) {
        await refreshUser();
      }
    } catch (err) {
      console.error("Failed to update tutorial status", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <div className="text-gray-500 dark:text-gray-400">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="no-scrollbar h-full bg-slate-50 dark:bg-gray-900 p-4 md:p-6 xl:max-w-none xl:px-10 xl:py-8 max-w-[1440px] mx-auto transition-colors duration-300 overflow-y-auto overflow-x-hidden"
    >

      {/* Sticky header — content scrolls beneath it */}
      <header className="sticky top-0 z-10 -mx-4 md:-mx-6 xl:-mx-10 px-4 md:px-6 xl:px-10 py-3 md:py-2.5 mb-5 md:mb-4 bg-slate-50/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              <span className="text-gray-900 dark:text-white">Hello, </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 dark:from-blue-400 dark:via-purple-400 dark:to-purple-500">
                {currentUser?.name || currentUser?.email?.split("@")[0] || "Trader"}
              </span>
              {currentUser?.is_premium && (
                <PremiumBadge size={18} className="ml-2 w-6 h-6 md:w-7 md:h-7" />
              )}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-sm md:text-base mt-1">
              Welcome to your Trading Hub
            </p>
          </div>

          {/* Header actions */}
          <div className="flex items-center">
            {/* Markets shortcut (mobile only — desktop uses the sidebar icon) */}
            {hasMarketAccess && (
              <button
                onClick={() => navigate('/dashboard/markets')}
                className="relative mr-3 p-3 bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700/50 rounded-full shadow-sm dark:shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700/70 transition-all md:hidden"
                title="Markets"
                aria-label="Open Markets"
              >
                <CandlestickChart size={20} className="text-gray-600 dark:text-gray-300" />
              </button>
            )}
            <div className="relative">
              <button
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  markAllAsRead();
                }}
                className="relative p-3 bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700/50 rounded-full shadow-sm dark:shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700/70 transition-all"
              >
                <Bell size={20} className="text-gray-600 dark:text-gray-300" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

                         {showNotifications && (
                <div
                  className="absolute top-12 right-0 w-80 md:w-96 max-h-96 overflow-y-auto bg-white dark:bg-gray-800/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/50 rounded-lg shadow-xl z-50"
                  style={{ scrollbarWidth: "none" }}
                >
                  <div className="p-4 md:p-5 border-b border-gray-200 dark:border-gray-700/50 flex justify-between items-center">
                    <h3 className="text-gray-900 dark:text-white font-medium">
                      Notifications
                    </h3>
                    {unreadCount > 0 && (
                      <button onClick={markAllAsRead} className="text-xs text-blue-500 hover:underline">
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="p-2">
                    {notifications.length > 0 ? (
                      notifications.map((n) => (
                        <NotificationItem
                          key={n.id}
                          notification={n}
                          onRemove={removeNotification}
                          onMarkRead={markAsRead}
                          navigate={navigate}
                        />
                      ))
                    ) : (
                      <div className="text-gray-400 text-center p-4 md:p-6">
                        No new notifications
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Profile avatar */}
            <button
              onClick={() => navigate('/dashboard/settings/profile')}
              className="ml-3 p-0.5 bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700/50 rounded-full shadow-sm dark:shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700/70 transition-all"
              title="Profile settings"
            >
              {currentUser?.avatar ? (
                <img
                  src={resolveUploadUrl(currentUser.avatar)}
                  alt={currentUser?.name || 'Profile'}
                  className="w-9 h-9 rounded-full object-cover"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold">
                  {(currentUser?.name || currentUser?.email || 'U').charAt(0).toUpperCase()}
                </div>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Desktop two-pane layout: account rail + tabs/content share the screen */}
      <div className="xl:grid xl:grid-cols-3 xl:gap-6 xl:items-start">
      {/* Account Overview */}
      <section className="mb-6 md:mb-6 xl:col-span-1 xl:row-span-2 xl:mb-0">
        <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-lg">
          <div className="p-4 md:p-6">
            <div className="flex items-center mb-3">
              <Wallet className="text-blue-500 mr-2" size={18} />
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
                Account Overview
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-2 gap-3 md:gap-4">
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 md:p-4 border border-gray-200 dark:border-gray-600/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Login</span>
                  <Settings size={14} className="text-gray-400" />
                </div>
                <div className="text-base md:text-xl font-bold text-gray-900 dark:text-white transition-all duration-500">
                  {displayAccount?.login ?? "—"}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 md:p-4 border border-gray-200 dark:border-gray-600/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Balance
                  </span>
                  <DollarSign size={14} className="text-gray-400" />
                </div>
                <div className="text-base md:text-xl font-bold text-gray-900 dark:text-white transition-all duration-500">
                  {displayAccount
                    ? `${fmt(displayAccount.balance)} ${displayAccount.currency ?? ""}`
                    : "—"}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 md:p-4 border border-gray-200 dark:border-gray-600/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Equity
                  </span>
                  <TrendingUp size={14} className="text-gray-400" />
                </div>
                <div className="text-base md:text-xl font-bold text-gray-900 dark:text-white transition-all duration-500">
                  {displayAccount
                    ? `${fmt(displayAccount.equity)} ${displayAccount.currency ?? ""}`
                    : "—"}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-3 md:p-4 border border-gray-200 dark:border-gray-600/30">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-500 dark:text-gray-400 text-sm">
                    Open P/L
                  </span>
                  <Activity size={14} className="text-gray-400" />
                </div>
                <div
                  className={`text-base md:text-xl font-bold transition-all duration-500 ${
                    displayAccount &&
                    (displayAccount.profit ?? displayAccount.equity - displayAccount.balance) >= 0
                      ? "text-green-500"
                      : "text-amber-500"
                  }`}
                >
                  {displayAccount
                    ? `${fmt(
                        displayAccount.profit ?? displayAccount.equity - displayAccount.balance
                      )} ${displayAccount.currency ?? ""}`
                    : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="mb-6 md:mb-6 xl:col-start-2 xl:col-span-2 xl:mb-0">
        <div className="flex justify-center items-center xl:justify-start">
          <div className="inline-flex rounded-lg bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700/50 p-2 shadow-sm dark:shadow-none">
            <button
              onClick={() => setActiveTab("history")}
              className={`flex items-center gap-2 py-3 md:py-2.5 px-4 md:px-5 rounded-lg transition-all duration-200 ${
                activeTab === "history"
                  ? "bg-blue-50 dark:bg-blue-600/10 text-blue-600 dark:text-white"
                  : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
            >
              <History size={18} />
              <span className="text-base md:text-lg font-medium">Trade History</span>
            </button>
            <button
              onClick={() => setActiveTab("journal")}
              className={`flex items-center gap-2 py-3 md:py-2.5 px-4 md:px-5 rounded-lg transition-all duration-200 ${
                activeTab === "journal"
                  ? "bg-blue-50 dark:bg-blue-600/10 text-blue-600 dark:text-white"
                  : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
            >
              <BookOpen size={18} />
              <span className="text-base md:text-lg font-medium">Journal</span>
            </button>
            <button
              onClick={() => setMetricsOpen(true)}
              className={`flex items-center gap-2 py-3 md:py-2.5 px-4 md:px-5 rounded-lg transition-all duration-200 ${
                activeTab === "analytics"
                  ? "bg-blue-50 dark:bg-blue-600/10 text-blue-600 dark:text-white"
                  : "text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
              }`}
            >
              <BarChart3 size={18} />
              <span className="text-base md:text-lg font-medium">Analytics</span>
            </button>
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="mb-24 md:mb-8 xl:col-start-2 xl:col-span-2 xl:mb-0">
        <div className="bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700/50 shadow-sm dark:shadow-lg">
          <div className="p-5 md:p-6">
            {activeTab === "history" && (
              <div className="space-y-3">
                <div className="flex justify-between items-center mb-2 px-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Recent Trades
                  </h3>
                  <span className="text-xs text-gray-400 dark:text-gray-500">
                    Closed Positions
                  </span>
                </div>
                {lastFiveTrades.length === 0 ? (
                  <div className="text-center text-gray-400 py-8 bg-gray-50 dark:bg-gray-800/20 rounded-lg border border-gray-200 dark:border-gray-700/50">
                    No closed trades found
                  </div>
                ) : (
                  lastFiveTrades.map((t, i) => {
                    const isBuy =
                      String(t.type || t.trade_type || "")
                        .toLowerCase()
                        .includes("buy") || String(t.type) === "0";
                    const rawProfit = Number(t.profit ?? t.profit_loss ?? 0);
                    const isProfit = rawProfit >= 0;
                    const symbol = t.symbol || t.pair || "UNKNOWN";
                    let displayTime = "—";
                    try {
                      const ts = t.close_time || t.time;
                      if (ts)
                        displayTime = new Date(
                          typeof ts === "number" ? ts * 1000 : ts
                        ).toLocaleString();
                    } catch (e) {}
                    const isOpen = expandedTicket === t.ticket;
                    return (
                      <div
                        key={t.ticket || i}
                        className="bg-gray-50 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50 rounded-lg overflow-hidden transition-all"
                      >
                        <div
                          className="flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-700/30 cursor-pointer"
                          onClick={() =>
                            setExpandedTicket(isOpen ? null : t.ticket)
                          }
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`p-1.5 rounded ${
                                isBuy
                                  ? "bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400"
                                  : "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400"
                              }`}
                            >
                              {isBuy ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                            </div>
                            <div>
                              <div className="text-gray-900 dark:text-white font-medium text-sm">
                                {symbol}
                              </div>
                              <div
                                className={`text-xs font-bold ${
                                  isBuy
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-red-600 dark:text-red-400"
                                }`}
                              >
                                {isBuy ? "BUY" : "SELL"}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <div
                                className={`text-sm font-bold ${
                                  isProfit
                                    ? "text-green-600 dark:text-green-400"
                                    : "text-amber-600 dark:text-amber-500"
                                }`}
                              >
                                {isProfit ? "+" : ""}
                                {fmt(rawProfit)}
                              </div>
                              <div className="text-[10px] text-gray-400 dark:text-gray-500">
                                {displayTime}
                              </div>
                            </div>
                            <ChevronDown
                              size={16}
                              className={`text-gray-400 transition-transform duration-300 ${
                                isOpen ? "rotate-180" : ""
                              }`}
                            />
                          </div>
                        </div>
                        {isOpen && (
                          <div className="p-3 border-t border-gray-200 dark:border-gray-700/50 bg-slate-50 dark:bg-gray-900/60">
                            <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
                              <div>
                                <span className="text-gray-400 block">Ticket</span>
                                <span className="text-gray-900 dark:text-white font-medium">
                                  {t.ticket || "—"}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-400 block">Volume</span>
                                <span className="text-gray-900 dark:text-white font-medium">
                                  {fmt(t.volume)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-400 block">Open Price</span>
                                <span className="text-gray-900 dark:text-white font-medium">
                                  {fmt(t.open_price)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-400 block">Close Price</span>
                                <span className="text-gray-900 dark:text-white font-medium">
                                  {fmt(t.close_price)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-400 block">Stop Loss</span>
                                <span className="text-red-500 font-medium">
                                  {fmt(t.sl)}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-400 block">Take Profit</span>
                                <span className="text-green-500 font-medium">
                                  {fmt(t.tp)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
            {activeTab === "journal" && (
              <div className="space-y-8 xl:grid xl:grid-cols-2 xl:gap-6 xl:space-y-0 xl:items-start">
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700/50 xl:pt-0 xl:border-t-0">
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    Behavior Analysis
                  </h3>
                                  <TradingBehaviorAnalysis alerts={behaviorAlerts} />
                </div>
                <div className="bg-gray-100 dark:bg-gray-700/30 rounded-xl p-5 md:p-6 border border-gray-200 dark:border-gray-600/30">
                  <h4 className="text-gray-900 dark:text-white text-lg font-medium mb-6">
                    Trading Statistics
                  </h4>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-6">
                    <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700/50 pb-3">
                      <span className="text-gray-500 dark:text-gray-400 text-base">
                        Today's Trades
                      </span>
                      <span className="text-gray-900 dark:text-white font-semibold text-lg">
                        {todayTrades.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700/50 pb-3">
                      <span className="text-gray-500 dark:text-gray-400 text-base">
                        Today's Win Rate
                      </span>
                      <span className="text-gray-900 dark:text-white font-semibold text-lg">
                        {todayWinRate}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700/50 pb-3">
                      <span className="text-gray-500 dark:text-gray-400 text-base">
                        Today's Profit
                      </span>
                      <span
                        className={`font-semibold text-lg ${
                          todayProfit >= 0 ? "text-green-500" : "text-amber-500"
                        }`}
                      >
                        {fmt(todayProfit)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700/50 pb-3">
                      <span className="text-gray-500 dark:text-gray-400 text-base">
                        Total Trades
                      </span>
                      <span className="text-gray-900 dark:text-white font-semibold text-lg">
                        {tradesArray.length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700/50 pb-3">
                      <span className="text-gray-500 dark:text-gray-400 text-base">
                        Total Win Rate
                      </span>
                      <span className="text-gray-900 dark:text-white font-semibold text-lg">
                        {winRate}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-200 dark:border-gray-700/50 pb-3">
                      <span className="text-gray-500 dark:text-gray-400 text-base">
                        Total Profit
                      </span>
                      <span
                        className={`font-semibold text-lg ${
                          totalProfit >= 0 ? "text-green-500" : "text-amber-500"
                        }`}
                      >
                        {fmt(totalProfit)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
      </div>

      {/* Overlays */}
      <AIDraggableChatButton onOpen={() => setChatOpen(true)} />
      <ChatModal
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        chatHistory={chatHistory}
        chatTyping={chatTyping}
        chatMessage={chatMessage}
        setChatMessage={setChatMessage}
        onSubmit={handleChatSubmit}
      />
      <MetricsModal
        isOpen={metricsOpen}
        onClose={() => setMetricsOpen(false)}
        trades={tradesArray}
        account={account}
      />
      {!loadingTutorial && (
        <Tutorial isVisible={showTutorial} onClose={handleTutorialClose} />
      )}
    </div>
  );
}
